import { useEffect, useState } from "react";
import { api } from "../lib/api";

const Bars = ({ title, items, testid }) => {
  const list = Array.isArray(items) ? items : [];
  const max = Math.max(1, ...list.map((i) => i.count));
  return (
    <div data-testid={testid} className="rounded-2xl border border-navy/10 bg-white p-5">
      <h3 className="font-display text-xl font-bold uppercase text-navy">{title}</h3>
      <div className="mt-4 space-y-2.5">
        {list.length === 0 && <p className="text-sm text-slate-400">No data yet</p>}
        {list.map((i, idx) => (
          <div key={`${i.label}-${idx}`} className="text-sm">
            <div className="flex justify-between gap-2"><span className="truncate text-slate-700">{i.label}</span><span className="font-semibold text-navy">{i.count}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand" style={{ width: `${(i.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const fmtTime = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

export default function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/analytics", { params: { days } }).then((r) => setD(r.data ?? {})).catch(() => setD({}));
  }, [days]);

  const daily = Array.isArray(d?.daily) ? d.daily : [];
  const maxDay = Math.max(1, ...daily.map((x) => x.count));
  const kpis = [["Visitors", d?.visitors], ["New", d?.new_visitors], ["Returning", d?.returning_visitors], ["Visits", d?.sessions], ["Avg. time", fmtTime(d?.avg_seconds ?? 0)]];

  return (
    <div data-testid="admin-analytics" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Counts only visitors who accepted analytics cookies.</p>
        <select data-testid="analytics-range" value={days} onChange={(e) => setDays(Number(e.target.value))} className="field !w-auto !py-2 text-sm">
          {[7, 30, 90, 365].map((n) => <option key={n} value={n}>Last {n} days</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {kpis.map(([l, v]) => (
          <div key={l} data-testid={`analytics-kpi-${l.toLowerCase().replace(/\W+/g, "-")}`} className="rounded-2xl border border-navy/10 bg-white p-5">
            <p className="font-display text-3xl font-extrabold text-navy">{v ?? 0}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-navy/10 bg-white p-5">
        <h3 className="font-display text-xl font-bold uppercase text-navy">Visits per day</h3>
        <div className="mt-4 flex h-40 items-end gap-1 overflow-x-auto">
          {daily.length === 0 && <p className="text-sm text-slate-400">No data yet</p>}
          {daily.map((x, idx) => (
            <div key={`${x.label}-${idx}`} title={`${x.label}: ${x.count}`} className="flex min-w-[10px] flex-1 flex-col items-center justify-end">
              <div className="w-full rounded-t bg-navy" style={{ height: `${(x.count / maxDay) * 100}%` }} />
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        <Bars testid="analytics-sources" title="Traffic sources" items={d?.sources} />
        <Bars testid="analytics-pages" title="Top pages" items={d?.pages} />
        <Bars testid="analytics-products" title="Products viewed" items={d?.products} />
        <Bars testid="analytics-locations" title="Approx. locations" items={d?.locations} />
        <Bars testid="analytics-devices" title="Devices" items={d?.devices} />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-navy/10 bg-white">
        <table data-testid="analytics-recent" className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-3">Time</th><th className="p-3">Visitor</th><th className="p-3">Source</th><th className="p-3">Device</th><th className="p-3">Location</th><th className="p-3">Pages</th><th className="p-3">Time spent</th><th className="p-3">Products</th></tr></thead>
          <tbody>
            {(Array.isArray(d?.recent) ? d.recent : []).map((v, i) => (
              <tr key={i} className="border-t border-navy/5">
                <td className="p-3 whitespace-nowrap">{new Date(v.started_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="p-3">{v.returning ? "Returning" : "New"}</td>
                <td className="p-3">{v.source}</td>
                <td className="p-3">{v.device}</td>
                <td className="p-3">{[v.city, v.region].filter(Boolean).join(", ") || "—"}</td>
                <td className="p-3">{v.pages}</td>
                <td className="p-3">{fmtTime(v.seconds ?? 0)}</td>
                <td className="p-3 text-xs">{(v.products || []).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
